/**
 * Scope-parameterised REST client for the Hybrid Timescale Reporting feature.
 *
 * Fetches reporting data from the Timeseries API REST endpoint at
 * GET {base}/{scope}/{id}/reporting, where {scope} is one of "systems",
 * "assets", or "customers". This generalises the previous systems-only
 * `reportingApi.ts` so the systems and assets sections can share one client.
 */

/** The reporting scope, mapped directly onto the REST path segment. */
export type ReportingScope = "systems" | "assets" | "customers"

/** Metadata returned by the reporting endpoint. */
export interface ReportingMeta {
  start: string
  stop: string
  frequency: string
  timezone: string
  row_count: number
  available_fields: string[]
  // Scope-specific identity fields (e.g. system_id/system_name, asset_id/
  // asset_name, customer_id/customer_name) are tolerated but not required by
  // callers.
  [key: string]: unknown
}

/** Full response shape from the reporting endpoint. */
export interface ReportingResponse {
  meta: ReportingMeta
  rows: Record<string, unknown>[]
}

/** Error thrown when the reporting API returns a non-OK response. */
export class ReportingApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = "ReportingApiError"
  }
}

/**
 * The base URL for the Timeseries API.
 * Falls back to the VITE_TIMESERIES_API_URL environment variable.
 */
const getDefaultBaseUrl = (): string => {
  return import.meta.env.VITE_TIMESERIES_API_URL ?? ""
}

/**
 * Fetches reporting data from the Timeseries API for a given scope and id.
 *
 * @param scope - The reporting scope: "systems" | "assets" | "customers"
 * @param id - The entity ID to fetch reporting data for
 * @param start - ISO 8601 start timestamp
 * @param stop - ISO 8601 stop timestamp
 * @param frequency - Time bucket frequency: 5min | hourly | daily | weekly | monthly
 * @param fields - Array of Display_Field_Keys to include in the response
 * @param token - Bearer token for authorization
 * @param tz - IANA timezone for bucket alignment (defaults to "UTC")
 * @param baseUrl - Optional base URL override for the Timeseries API
 * @returns Typed ReportingResponse with meta and rows
 * @throws ReportingApiError on 400, 403, or other non-OK responses
 */
export async function fetchReportingData(
  scope: ReportingScope,
  id: string,
  start: string,
  stop: string,
  frequency: string,
  fields: string[],
  token: string,
  tz: string = "UTC",
  baseUrl?: string,
  signal?: AbortSignal,
): Promise<ReportingResponse> {
  const base = baseUrl ?? getDefaultBaseUrl()

  const url = new URL(`${base}/${scope}/${id}/reporting`)
  url.searchParams.set("start", start)
  url.searchParams.set("stop", stop)
  url.searchParams.set("frequency", frequency)
  url.searchParams.set("fields", fields.join(","))
  url.searchParams.set("tz", tz)

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    "X-Auth-Type": "better-auth",
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
    signal,
  })

  if (response.ok) {
    const data: ReportingResponse = await response.json()
    return data
  }

  if (response.status === 400) {
    const errorBody = await response.json().catch(() => null)
    const message = errorBody?.detail ?? "Invalid request parameters"
    throw new ReportingApiError(message, 400)
  }

  if (response.status === 403) {
    const errorBody = await response.json().catch(() => null)
    const message = errorBody?.detail ?? "You don't have access to this reporting data."
    throw new ReportingApiError(message, 403)
  }

  // All other errors (500, etc.)
  throw new ReportingApiError("Something went wrong. Please try again later.", response.status)
}
